import { Controller as BaseController } from "@hotwired/stimulus";
import Uppy from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import AwsS3 from "@uppy/aws-s3";
import Dashboard from "@uppy/dashboard";
import Webcam from "@uppy/webcam";
import GoldenRetriever from "@uppy/golden-retriever";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";

// There is an open issue about the interaction between Golden Retriever and
// aws-s3-multipart: https://github.com/transloadit/uppy/issues/5230
export class Controller extends BaseController {
  static targets = ["input", "submit"];
  static values = {
    service: String,
    types: Array,
    action: String,
    dimensions: Array,
    debug: Boolean,
  };

  connect() {
    this.submitTarget.classList.add("hidden");
    this.inputTarget.classList.add("hidden");

    this.uppy = new Uppy({
      debug: this.debugValue,
      id: this.inputTarget.id,
      allowMultipleUploadBatches: true,
      restrictions: {
        allowedFileTypes: this.typesValue,
      },
    });

    if (this.serviceValue == "s3") {
      this.uppy.use(AwsS3, {
        endpoint: "/", // will call Shrine"s presign endpoint mounted on `/s3/params`
        shouldUseMultipart: false,
      });
    } else if (this.serviceValue == "s3_multipart") {
      this.uppy.use(AwsS3, {
        endpoint: "/", // will call this.uppy-s3_multipart endpoint mounted on `/s3/multipart`
        shouldUseMultipart: (file) => {
          return file.size > 100 * 2 ** 20; // 100MB
        },
      });
    } else {
      this.uppy.use(XHRUpload, {
        endpoint: "/upload", // Shrine"s upload endpoint
      });
    }

    this.uppy.use(Dashboard, {
      target: this.inputTarget.parentNode,
      inline: true,
      height: "auto",
      width: "100%",
    });

    this.uppy.use(GoldenRetriever, {
      serviceWorker: true,
      // indexedDB: {
      //   maxFileSize: 1_000 * 0x10_00_00,
      //   maxTotalSize: 10_000 * 0x10_00_00
      // }
    })

    this.uppy.use(Webcam, {
      target: Dashboard,
      showVideoSourceDropdown: true,
      showRecordingLength: true,
    });

    this.uppy.on("upload-success", (file, response) =>
      this._sendToServer(file, response),
    );
  }

  async _sendToServer(file, response) {
    const action = this.actionValue;

    // Fetch to PATCH this to the server
    try {
      const serverResponse = await fetch(action, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
        body: this._uploadedFileData(file, response, this.serviceValue),
      });

      const responseStatus = serverResponse.status;
      const responseBody = await serverResponse.json();

      if (responseStatus > 400) {
        this.uppy.info(responseBody.errors, "error");
        this.uppy.removeFile(file.id);
      }
    } catch (error) {
      this.uppy.info(error.message, "error");
      this.uppy.removeFile(file.id);
    }
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.destroy();
    }
  }

  _uploadedFileData(file, response, server) {
    if (server == "s3") {
      const id = response.body.key.match(/^cache\/(.+)/)[1]; // object key without prefix

      return JSON.stringify(this._fileData(file, id));
    } else if (server == "s3_multipart") {
      let cacheKey = response.body.key;
      let id = (cacheKey ?? file.s3Multipart.key).match(/^cache\/(.+)/)[1];

      return JSON.stringify(this._fileData(file, id));
    } else {
      return JSON.stringify(response.body);
    }
  }

  _fileData(file, id) {
    return {
      id: id,
      storage: "cache",
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    };
  }
}
